import React, { useEffect, useState } from 'react';
import axios from 'axios';
import AddInvitationTypeForm from './AddInvitationTypeForm';
import EditInvitationTypeForm from './EditInvitationTypeForm';
import '../css/ParticipantList.css';
import '../css/InvitationTypeList.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTrashAlt, faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';

const InvitationTypeList = () => {
  const [types, setTypes] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [typeToEdit, setTypeToEdit] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const API_URL = process.env.REACT_APP_API_URL;

  const fetchTypes = async () => {
    try {
      const res = await axios.get(`${API_URL}/ems/invitation-types`);
      if (Array.isArray(res.data)) setTypes(res.data);
    } catch (err) {
      console.error('Failed to fetch invitation types', err);
    }
  };

  useEffect(() => {
    fetchTypes();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this invitation type?')) return;
    try {
      await axios.delete(`${API_URL}/ems/invitation-types/${id}`);
      alert('Invitation type deleted successfully', 'success');
      fetchTypes();
    } catch (err) {
      const msg = err.response?.data?.message || 'Delete failed';
      alert(msg, 'danger');
    }
  };

  const filtered = types.filter((t) =>
    (t.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key ? (prev.direction === 'asc' ? 'desc' : 'asc') : 'asc',
    }));
  };

  const sortIcon = (key) => {
    if (sortConfig.key !== key) return faSort;
    return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
  };

  const sorted = sortConfig.key
    ? [...filtered].sort((a, b) => {
        const aVal = (a.name || '').toLowerCase();
        const bVal = (b.name || '').toLowerCase();
        const result = aVal.localeCompare(bVal);
        return sortConfig.direction === 'asc' ? result : -result;
      })
    : filtered;

  const effectivePerPage = itemsPerPage >= sorted.length ? Math.max(sorted.length, 1) : itemsPerPage;
  const totalPages = Math.max(Math.ceil(sorted.length / effectivePerPage), 1);
  const indexOfLast = currentPage * effectivePerPage;
  const indexOfFirst = indexOfLast - effectivePerPage;
  const currentItems = sorted.slice(indexOfFirst, indexOfLast);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const getVisiblePages = () => {
    if (totalPages <= 5) return pageNumbers;
    if (currentPage <= 3) return pageNumbers.slice(0, 5);
    if (currentPage >= totalPages - 2) return pageNumbers.slice(-5);
    return pageNumbers.slice(currentPage - 3, currentPage + 2);
  };

  return (
    <div className="ems-invitation-types-page p-4">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center w-25">
          <div className="me-2" style={{ flex: '1' }}>
            <input
              type="text"
              className="form-control search-input"
              placeholder="Search invitation types..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              title="Search invitation types"
              style={{ border: '1px solid #d1cfcf' }}
            />
          </div>
        </div>
        <button
          type="button"
          onClick={() => setShowAddModal(true)}
          style={{
            background: '#000',
            color: 'white',
            padding: '8px 16px',
            borderRadius: '5px',
            border: 'none',
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}
        >
          + Add Invitation Type
        </button>
      </div>

      <div className="invitation-type-header">
        <div
          className={`sort-header ${sortConfig.key === 'name' ? 'sorted' : ''}`}
          onClick={() => toggleSort('name')}
        >
          Name <FontAwesomeIcon icon={sortIcon('name')} className="sort-icon" />
        </div>
        <div>Actions</div>
      </div>

      {filtered.length === 0 ? (
        <div className="p-4 text-center text-muted">No invitation types available</div>
      ) : (
        currentItems.map((t) => (
          <div className="invitation-type-row" key={t._id}>
            <div>{t.name || '—'}</div>
            <div className="invitation-type-actions">
              <button
                type="button"
                className="btn btn-sm action-btn"
                onClick={() => setTypeToEdit(t)}
                title="Edit invitation type"
              >
                <FontAwesomeIcon icon={faPen} />
              </button>
              <button
                type="button"
                className="btn btn-sm action-btn"
                onClick={() => handleDelete(t._id)}
                title="Delete invitation type"
              >
                <FontAwesomeIcon icon={faTrashAlt} />
              </button>
            </div>
          </div>
        ))
      )}

      {sorted.length > 0 && (
        <div className="participant-pagination">
          <div className="d-flex align-items-center gap-2">
            <span style={{ fontSize: '13px', color: '#666' }}>Show</span>
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto', fontSize: '13px' }}
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <div className="pagination-box">
            <button
              type="button"
              className="pagination-btn"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              Previous
            </button>

            {getVisiblePages().map((num) => (
              <button
                key={num}
                type="button"
                className={`pagination-btn ${num === currentPage ? 'active' : ''}`}
                onClick={() => setCurrentPage(num)}
              >
                {num}
              </button>
            ))}

            <button
              type="button"
              className="pagination-btn"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {showAddModal && (
        <AddInvitationTypeForm onClose={() => setShowAddModal(false)} onAdded={fetchTypes} />
      )}

      {typeToEdit && (
        <EditInvitationTypeForm
          onClose={() => setTypeToEdit(null)}
          onUpdated={fetchTypes}
          typeToEdit={typeToEdit}
        />
      )}
    </div>
  );
};

export default InvitationTypeList;
